import { useContext, useEffect, useRef, useState, useMemo } from 'react';
import ResponseErrorTypes from '../../../../constants/ResponseErrorTypes';
import ResponseStatus from '../../../../constants/ResponseStatus';
import {
    getErrorMessage,
    getFirstError,
    showErrorNotificationFromResponse,
} from '../../../../services/errorUtils';
import LoadingScreenContext from '../../../../services/LoadingScreenContext';
import AuthenticatedRouteLanguageContext from '../../../../services/AuthenticatedRouteLanguageContext';
import useNetworkErrorNotification from '../../../../services/useNetworkErrorNotification';
import hasValidASCIICharacters from '../../../../services/hasValidASCIICharacters';
import RecaptchaV2Invisible from '../../../../components/RecaptchaV2Invisible';
import { useUserSessionContext } from '../../../../components/UserSessionContextWrapper';
import HelpIcon from '../../../../components/HelpIcon';
import EmailField from '../../../../components/EmailField';
import InvalidUsernameCharactersInlineMessage from '../../../../components/InvalidUsernameCharactersInlineMessage';
import { isValidEmail } from '../../../../services/emailUtils';
import { requestEmailUpdate } from '../../myAccountApi';
import Footer from '../Footer';

function EmailEdit({ currentEmail, onCancel, onSuccess, labels }) {
    const [newEmail, setNewEmail] = useState(currentEmail);
    const [errorMessage, setErrorMessage] = useState();
    const { handleResponseSessionErrors } = useUserSessionContext();
    const { showNotification: showNetworkErrorNotification } = useNetworkErrorNotification();
    const resetCaptcha = useRef();
    const executeCaptcha = useRef();
    const { setLoading } = useContext(LoadingScreenContext);
    const { messageLanguage } = useContext(AuthenticatedRouteLanguageContext);

    const invalidUsernameCharsError = useMemo(
        () => <InvalidUsernameCharactersInlineMessage show />,
        [],
    );

    const onEmailChange = (email) => {
        setNewEmail(email);
        setErrorMessage('');
    };

    const setExecuteRef = (callback) => {
        executeCaptcha.current = callback;
    };

    const setResetRef = (callback) => {
        resetCaptcha.current = callback;
    };

    useEffect(() => {
        setErrorMessage('');
    }, [messageLanguage]);

    const handleVerify = async () => {
        if (!isValidEmail(newEmail)) {
            setErrorMessage(labels.invalidEmailError);
            return;
        }

        if (!hasValidASCIICharacters(newEmail)) {
            setErrorMessage(invalidUsernameCharsError);
            return;
        }

        setErrorMessage('');
        const token = await executeCaptcha.current?.();
        setLoading(true);
        const response = await requestEmailUpdate(newEmail, token);
        setLoading(false);
        resetCaptcha.current?.();

        if (handleResponseSessionErrors(response)) {
            return;
        }

        switch (response.responseStatus) {
            case ResponseStatus.SUCCESS:
                onSuccess(newEmail);
                break;
            case ResponseStatus.FAILURE: {
                const responseError = getFirstError(response);
                switch (responseError.type) {
                    case ResponseErrorTypes.VEEVAID_EMAIL_NOT_AVAILABLE:
                    case ResponseErrorTypes.VEEVAID_INVALID_RECAPTCHA: {
                        const message = await getErrorMessage(responseError, messageLanguage);
                        setErrorMessage(message);
                        break;
                    }
                    default: {
                        const i18nOptions = { language: messageLanguage };
                        await showErrorNotificationFromResponse(response, i18nOptions);
                    }
                }
                break;
            }
            case ResponseStatus.UI_NETWORK_ERROR:
                await showNetworkErrorNotification();
                break;
            default:
            // No-op
        }
    };

    return (
        <>
            <RecaptchaV2Invisible reset={setResetRef} execute={setExecuteRef} />
            <EmailField
                label={
                    <div>
                        {labels.primaryEmailLabel}{' '}
                        <HelpIcon message={labels.usernameTooltipLabel} />
                    </div>
                }
                value={newEmail}
                onChange={onEmailChange}
                errorMessage={errorMessage}
            />
            <Footer
                disableSubmit={newEmail === currentEmail}
                onCancel={onCancel}
                onSubmit={handleVerify}
                cancelLabel={labels.cancelLabel}
                submitLabel={labels.verifyLabel}
            />
        </>
    );
}

export default EmailEdit;
